<template>
  <el-table
    :border="border"
    tooltip-effect="light"
    :expand-row-keys="expand ? expandRowKeys : undefined"
    @expand-change="expandChange"
    :row-key="rowKey"
    :data="tableData"
    :lazy="lazy"
    :load="load"
    :default-expand-all="defaultExpandAll"
    style="width: 100%"
  >
    <el-table-column v-if="expand" type="expand">
      <template v-slot="scope">
        <slot
          name="expand"
          :expanded="expandRowKeys.includes(scope.row[rowKey])"
          v-bind="scope"
        />
      </template>
    </el-table-column>
    <el-table-column
      v-for="col in visibleColumns"
      :key="col.dataIndex"
      :label="col.label"
      :prop="col.dataIndex"
      :width="col.width"
      :minWidth="col.minWidth"
      :expand="col.expand"
      :fixed="col.fixed"
      :showOverflowTooltip="col.showOverflowTooltip"
    >
      <template slot-scope="scope">
        <el-link
          v-if="col.click"
          type="primary"
          @click="() => col.click(scope.row)"
          >{{ scope.row[col.dataIndex] }}</el-link
        >
        <el-link
          v-else-if="col.router"
          type="primary"
          :href="getColLink(col.router, scope.row)"
          :target="getTarget(col.router)"
          >{{ scope.row[col.dataIndex] }}</el-link
        >
        <span v-else-if="col.render"
          ><slot name="cell" :row="scope.row" :config="col"
        /></span>
        <span v-else-if="col.type === 'status'">{{
          col.map[scope.row[col.dataIndex]] || col.dataIndex
        }}</span>
        <col-enum
          v-else-if="col.type === 'enum'"
          :items="col.items"
          :key="scope.row[col.dataIndex]"
          :value="scope.row[col.dataIndex]"
        />
        <col-edit
          :showOverflowTooltip="col.showOverflowTooltip"
          v-else-if="col.type === 'edit'"
          :content="scope.row[col.dataIndex]"
          :required="col.update.required"
          :validate="col.update.validate"
          :maxlength="col.update.maxlength"
          @update="
            (content, callback) =>
              updateColumn(content, callback, col, scope.row)
          "
        />
        <span v-else>{{ transColText(scope.row[col.dataIndex], col) }}</span>
      </template>
      <template v-slot:header v-if="col.tip">
        <span>{{ col.label }}</span>
        <i
          style="margin-left: 3px"
          class="el-icon-info"
          v-tooltip.show="col.tip"
        ></i>
      </template>
    </el-table-column>

    <!-- 操作列 -->
    <el-table-column
      v-if="visibleOperations.length > 0"
      label="操作"
      :width="getOptColWidth()"
      class-name="mytable-opt-cell"
      :fixed="_optFixed"
    >
      <template slot-scope="custom">
        <span
          v-for="item in getOptItems(custom.row)"
          :key="item.name"
          :data-cy="item.name"
          size="mini"
          :type="item.type || 'info'"
          :class="{
            'link-btn': true,
            disabled: isDisableItem(custom.row, item) === 'visible',
          }"
          @click="() => optHandleCommand(custom.row, item)"
          >{{ getItemLabel(item, custom.row) }}</span
        >
        <cs-dropdown
          :row="custom.row"
          :items="getOptDropdownItems(custom.row)"
          :render-width-no-data="false"
          class-name="link-btn"
          :on-selected="(item) => optHandleCommand(custom.row, item)"
        />
      </template>
    </el-table-column>
  </el-table>
</template>

<script>
import Log from "@/utils/log";
import { isEmpt, dateFormat } from "@/utils/utils";
import permission from "./permission";

import CsDropdown from "../MyDropdown";
import Common, { EMPT_TEXT } from "./common";

import ColEdit from "./cols/editcol.vue";
import ColEnum from "./cols/enum.vue";

// 后台数据转换为列表中显示的格式
const TransMap = {
  datetime: (sText, oCol) => {
    if (isEmpt(sText)) {
      return EMPT_TEXT;
    }
    const FormatMap = {
      date: () => dateFormat(sText, "date"),
      time: () => dateFormat(sText, "time"),
      full: () => dateFormat(sText, "full"),
    };
    return (FormatMap[oCol.format] || FormatMap.full)();
  },
  text: (sText) => (isEmpt(sText) ? EMPT_TEXT : sText),
};

// 操作列显示的操作项最大个数。当超过2项时，会多一个更多的选项，收纳后续的全部操作项。当超过1项时全部显示
const OPT_MAX_ITEM = 3;

function getColWidthByType(sType) {
  const TypeWidth = {
    datetime: 152,
  };
  return TypeWidth[sType] || "auto";
}

const log = Log("CsTable-table");
export default {
  components: { ColEnum, ColEdit, CsDropdown },
  mixins: [permission],
  props: {
    border: {
      type: Boolean,
      default: true,
    },
    // 列定义，包含显示的列和不显示的列。但不包含操作列
    columns: {
      type: Array,
      default: () => [],
    },

    tableData: {
      type: Array,
      default: () => [],
    },

    optColWidth: {
      type: [String, Number],
      default: "auto",
    },

    optFixed: {
      type: [String, Boolean],
      required: false,
      default: false,
    },

    // 操作列中的操作按钮定义，如果没有操作按钮，则不显示操作列
    operations: {
      type: Array,
      default: () => [],
    },
    expand: {
      type: Boolean,
      required: false,
      default: false,
    },
    rowKey: {
      type: String,
      required: false,
      default: undefined,
    },
    lazy: {
      type: Boolean,
      required: false,
      default: undefined,
    },
    load: {
      type: Function,
      required: false,
      default: undefined,
    },
    defaultExpandAll: {
      type: Boolean,
      required: false,
      default: false,
    },
    expandRowKeys: {
      type: Array,
      required: false,
      default() {
        return [];
      },
    },
  },
  computed: {
    // 返回显示在列表中的列
    // 在页面中定义时会有部分列不需要显示但需要搜索，可以设置为visible:false
    visibleColumns() {
      return this.columns
        .filter((col) => col.visible !== false)
        .map((col) => ({
          ...col,
          label: col.label || Common.getText(col.name),
          width: col.width || getColWidthByType(col.type),
        }));
    },

    visibleOperations() {
      return this.operations
        .filter((item) => this.hasPermission(item.permission))
        .map((item) => ({
          ...item,
          label: item.label || Common.getText(item.name),
        }));
    },
    _optFixed() {
      if (this.optFixed === true) {
        return "right";
      } else {
        return this.optFixed;
      }
    },
  },
  methods: {
    expandChange(row, expandedRows) {
      if (this.rowKey === undefined) {
        console.error("展开表格必须配置rowKey");
        return;
      }
      if (Array.isArray(expandedRows)) {
        const expandRowKeys = expandedRows.map((item) => item[this.rowKey]);
        this.$emit("update:expandRowKeys", expandRowKeys);
      }
    },
    isDisableItem(record, optItem) {
      if (!optItem.disable) {
        return false;
      }
      return optItem.disable(record);
    },
    getOptItems(row) {
      const items = this.visibleOperations.filter(
        (item) => this.isDisableItem(row, item) !== true
      );
      // 超过三项时只展示前两项，第三项为下拉更多
      if (items.length > OPT_MAX_ITEM + 1) {
        items.length = OPT_MAX_ITEM;
      }
      return items;
    },
    // 获取操作列中需要折叠起来的项
    getOptDropdownItems(row) {
      const allItems = this.visibleOperations.filter(
        (item) => this.isDisableItem(row, item) !== true
      );
      if (allItems.length <= OPT_MAX_ITEM + 1) {
        return [];
      }

      // 返回第二项及之后的数据
      return allItems.filter((item, index) => index + 1 > OPT_MAX_ITEM);
    },
    getItemLabel(item, row) {
      return Common.getRealValue(item.label, row);
    },
    getEnumText(items, value) {
      if (!items.find) {
        return value;
      }

      const curItem = items.find((item) => item.value === value) || {};
      return curItem.text || value;
    },
    getOptColWidth() {
      if (this.optColWidth !== "auto") {
        log.debug("opt width:", this.optColWidth);
        return this.optColWidth;
      }

      let moreWidth = 0;
      const allItems = this.visibleOperations;

      const textCols = [...allItems];
      if (allItems.length > OPT_MAX_ITEM + 1) {
        textCols.length = OPT_MAX_ITEM;
        textCols.push({ label: "更多" });
        moreWidth = 19;
      }

      const len = textCols
        .map((col) => Common.getRealValue(col.label, {}))
        .join("")
        // eslint-disable-next-line no-control-regex
        .replace(/[^\x00-\xFF]/g, "FF").length;
      const baseWidth = textCols.length * 10; // 每项后面会有10px的间隔

      // 字符个数*字符宽7 + 间隔 + 更多的下拉箭头 + 外层padding + 留白
      return len * 8 + baseWidth + moreWidth + 20 + 2;
    },

    // 检查执行结果，并判断是否要刷新列表
    checkAndRefreshTable(item, result) {
      if (item.refresh !== true) {
        // 如果不需要自动刷新，则返回
        return;
      }

      const refreshTable = () => this.$emit("refresh");

      // 没有异步处理，直接刷新列表
      if (!result || !result.then) {
        refreshTable();
        return;
      }

      // 处理结果是promise，需要等到异步处理完成后刷新列表
      result.then(refreshTable);
    },

    transColText(sText, oCol) {
      const fnTrans = TransMap[oCol.type] || TransMap.text;
      return fnTrans(sText, oCol);
    },

    // 生成超链接串
    // 页面需要指定跳转的路由（不带前缀#）和对应的参数（是一个函数，根据行数据返回参数对象）
    // 如果没有参数时可以只是一个路由字符串
    // 参数例子：
    //    1。 只有一个路由：'/app/team'
    //    2.  带参数的路由：{path: '/app/team', params: record=>({name: record.name, date: record.date})}
    getColLink(router, record) {
      const sRouter = router.path || router;
      const params = router.params ? router.params(record) : {};
      const aParam = Object.keys(params).map(
        (name) => `${name}=${encodeURIComponent(params[name])}`
      );
      const sParam = aParam.length > 0 ? `?${aParam.join("&")}` : "";
      const sPath = /^http(s)?:\/\//.test(sRouter) ? sRouter : `#${sRouter}`;
      return `${sPath}${sParam}`;
    },
    getTarget(router) {
      // 如果指定了全路径，则在新页签中打开链接
      const sRouter = router.path || router;
      return /^http(s)?:\/\//.test(sRouter) ? "_blank" : undefined;
    },
    // 操作列按钮的点击处理
    optHandleCommand(record, item) {
      if (this.isDisableItem(record, item)) {
        return;
      }

      // dispatch command: action > path
      if (item.action) {
        if (item.confirmMsg) {
          const sConfirm = Common.getRealValue(item.confirmMsg, record);
          this.$confirm(sConfirm, "提示", {
            confirmButtonText: "确定",
            cancelButtonText: "取消",
            type: "warning",
          }).then(() => {
            this.checkAndRefreshTable(item, item.action(record, item));
          });
        } else {
          this.checkAndRefreshTable(item, item.action(record, item));
        }
        return;
      }

      if (item.router) {
        const { router } = item;
        let oPath = router;
        if (typeof router === "string") {
          oPath = { router };
        }

        const { path, params } = oPath;
        this.$router.push({
          path,
          query: typeof params === "function" ? params(record) : params,
        });
        return;
      }

      return;
    },

    // 编辑单元格保存操作
    updateColumn(content, callback, column, row) {
      if (column.update) {
        const upateFunc =
          typeof column.update === "function"
            ? column.update
            : column.update.handler;
        const promise = upateFunc(content, row);
        if (Object.prototype.toString.call(promise) === "[object Promise]") {
          promise
            .then(() => {
              row[column.dataIndex] = content;
            })
            .finally(() => {
              callback();
            });
        } else {
          log.error("可编辑单元格update方法需要返回Promise");
        }
      } else {
        log.error("可编辑单元格需要提供update方法");
      }
    },
  },
};
</script>
